import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../AppContext";
import mainAxios from "../../services/mainAxios";
import func from "../../services/functions";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileInvoice, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import "./lastPurchaseInvoices.scss";

const LastPurchaseInvoices = ({ inView = true }) => {
  const { t } = useTranslation("translations");
  const {
    user: [user],
    selectedCompany: [selectedCompany],
  } = useAppContext();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!inView) return;
    const fetchInvoices = async () => {
      setLoading(true);
      const toDate = moment().endOf("day").toISOString();
      const fromDate = moment().subtract(1, "month").startOf("day").toISOString();
      const nuis = user.companiesRights.find(
        (company) => company.id === parseInt(selectedCompany)
      )?.nuis;

      try {
        const res = await mainAxios.post("endpoint/v2/getMyPurchases", {
          fromDate,
          toDate,
          value: null,
          params: {
            dataFrom: "DB",
            search: "einvoice",
            internalStatus: "Accepted",
            checkUnknownBusiness: false,
            purchaseStatus: "ACCEPTED"
          },
          pagination: {
            pageSize: 5,
            pageNumber: 1
          },
          nuis,
          sortCriteria: [
            {
              field: "recordDateTime",
              direction: "DESC"
            }
          ]
        });

        if (res?.data?.status && res.data?.content) {
          setInvoices(res.data.content);
        }
      } catch (err) {
        console.error("Failed to fetch purchase invoices", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();

  }, [inView, user?.username, selectedCompany]);

  const SkeletonRow = () => (
    <div className="lpi-skeleton-row">
      <div className="lpi-skeleton-cell lpi-skeleton-cell--supplier">
        <div className="lpi-shimmer" style={{ width: "70%", height: 16 }} />
        <div className="lpi-shimmer" style={{ width: "50%", height: 12, marginTop: 6 }} />
      </div>
      <div className="lpi-skeleton-cell lpi-skeleton-cell--doc">
        <div className="lpi-shimmer" style={{ width: "80%", height: 14 }} />
      </div>
      <div className="lpi-skeleton-cell lpi-skeleton-cell--date">
        <div className="lpi-shimmer" style={{ width: "90%", height: 14 }} />
      </div>
      <div className="lpi-skeleton-cell lpi-skeleton-cell--amount">
        <div className="lpi-shimmer" style={{ width: "100%", height: 16 }} />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="lpi-card">
        <div className="lpi-header">
          <h3>{t("dashboard.lastPurchaseInvoices") || "Last Purchase Invoices"}</h3>
        </div>
        <div className="lpi-table">
          <div className="lpi-table-header">
            <div className="lpi-th lpi-th--supplier">{t("purchases.supplierName")}</div>
            <div className="lpi-th lpi-th--doc">{t("invoice.docNumber")}</div>
            <div className="lpi-th lpi-th--date">{t("table.issueDatetime")}</div>
            <div className="lpi-th lpi-th--amount">{t("dashboard.total")}</div>
          </div>
          <div className="lpi-table-body">
            {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="lpi-card">
        <div className="lpi-header">
          <h3>{t("dashboard.lastPurchaseInvoices") || "Last Purchase Invoices"}</h3>
        </div>
        <div className="lpi-empty">
          <div className="lpi-empty__icon">
            <FontAwesomeIcon icon={faFileInvoice} />
          </div>
          <div className="lpi-empty__title">{t("dashboard.noData")}</div>
          <div className="lpi-empty__text">{t("dashboard.noPurchaseInvoices") || "No purchase invoices found"}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="lpi-card">
      <div className="lpi-header">
        <h3>{t("dashboard.lastPurchaseInvoices") || "Last Purchase Invoices"}</h3>
        <Link to="/purchases" className="lpi-view-all">
          {t("common.viewAll") || "View All"}
          <FontAwesomeIcon icon={faExternalLinkAlt} />
        </Link>
      </div>
      <div className="lpi-table">
        <div className="lpi-table-header">
          <div className="lpi-th lpi-th--supplier">{t("purchases.supplierName")}</div>
          <div className="lpi-th lpi-th--doc">{t("invoice.docNumber")}</div>
          <div className="lpi-th lpi-th--date">{t("table.issueDatetime")}</div>
          <div className="lpi-th lpi-th--amount">{t("dashboard.total")}</div>
        </div>
        <div className="lpi-table-body">
          {invoices.map((invoice, idx) => {
            const supplierName = invoice?.connectionTransactional?.name || invoice?.sellerName || invoice?.sellerTin || "-";
            return (
              <Link
                to={`/edit-purchase/${invoice.id}`}
                key={invoice.id || idx}
                className="lpi-row"
              >
                <div className="lpi-cell lpi-cell--supplier">
                  <span className="lpi-supplier-name" title={supplierName}>
                    {supplierName}
                  </span>
                  <span className="lpi-supplier-nuis">{invoice?.sellerTin || ""}</span>
                </div>
                <div className="lpi-cell lpi-cell--doc">
                  <span className="lpi-doc-number" title={invoice?.docNumber}>
                    {invoice?.docNumber || "-"}
                  </span>
                </div>
                <div className="lpi-cell lpi-cell--date">
                  {invoice?.recordDateTime
                    ? moment(invoice.recordDateTime).format("DD/MM/YYYY")
                    : "-"}
                </div>
                <div className="lpi-cell lpi-cell--amount">
                  <span className="lpi-amount">
                    {func.getFormattedMoneyValue(invoice?.amount || 0)}
                  </span>
                  <span className="lpi-currency">{invoice?.currency || "ALL"}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LastPurchaseInvoices;
